<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import googleClient, {
  isCalendarDisconnected,
  rememberDisconnectedCalendar,
} from 'dashboard/api/channel/googleClient';
import { format, parseISO, addDays } from 'date-fns';

const { t } = useI18n();
const { accountId } = useAccount();

const isLoading = ref(true);
const isConnected = ref(false);
const events = ref([]);
const selectedEvent = ref(null);

const currentChat = useMapGetter('getSelectedChat');
const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));

const contactEmail = computed(
  () => contact.value?.email?.toLowerCase() || null
);
const contactName = computed(() => contact.value?.name?.toLowerCase() || null);

const contactEvents = computed(() => {
  if (!contactEmail.value && !contactName.value) return events.value;
  return events.value.filter(event => {
    const attendees = event.attendees || [];
    const matchByEmail =
      contactEmail.value &&
      attendees.some(a => a.email?.toLowerCase() === contactEmail.value);
    const matchByName =
      contactName.value &&
      attendees.some(a =>
        a.displayName?.toLowerCase().includes(contactName.value)
      );
    const matchInSummary =
      contactName.value &&
      event.summary?.toLowerCase().includes(contactName.value);
    return matchByEmail || matchByName || matchInSummary;
  });
});

const allEvents = computed(() => {
  return events.value.filter(e => !contactEvents.value.includes(e));
});

const formatTime = dateStr => {
  if (!dateStr) return '';
  try {
    return format(
      dateStr.includes('T')
        ? parseISO(dateStr)
        : parseISO(`${dateStr}T00:00:00`),
      'HH:mm'
    );
  } catch {
    return '';
  }
};

const formatDate = dateStr => {
  if (!dateStr) return '';
  try {
    const d = dateStr.includes('T') ? parseISO(dateStr) : parseISO(dateStr);
    return format(d, 'dd/MM');
  } catch {
    return '';
  }
};

const isUpcoming = event => {
  const date = event.start.dateTime
    ? parseISO(event.start.dateTime)
    : parseISO(event.start.date);
  return date >= new Date();
};

const fetchEvents = async () => {
  if (isCalendarDisconnected(accountId.value)) {
    isConnected.value = false;
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  try {
    const now = new Date();
    const { data } = await googleClient.fetchCalendarEvents({
      timeMin: now.toISOString(),
      timeMax: addDays(now, 30).toISOString(),
    });
    events.value = data.items || [];
    isConnected.value = true;
  } catch (error) {
    if (error.response?.data?.error === 'Google Calendar not connected') {
      rememberDisconnectedCalendar(accountId.value);
    }
    isConnected.value = false;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchEvents);
</script>

<template>
  <div class="flex flex-col gap-1">
    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center py-3">
      <span class="i-lucide-loader-2 size-4 text-n-slate-10 animate-spin" />
    </div>

    <!-- Not connected -->
    <div v-else-if="!isConnected" class="py-2 text-xs text-n-slate-10">
      {{ t('CALENDAR.SIDEBAR.NOT_CONNECTED') }}
    </div>

    <template v-else>
      <!-- Event detail -->
      <div v-if="selectedEvent" class="flex flex-col gap-2">
        <button
          class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12"
          @click="selectedEvent = null"
        >
          <span class="i-lucide-arrow-left size-3" />
          {{ t('CALENDAR.BACK') }}
        </button>
        <div class="text-xs font-medium text-n-slate-12">
          {{ selectedEvent.summary }}
        </div>
        <div class="flex items-center gap-1.5 text-xs text-n-slate-10">
          <span class="i-lucide-clock size-3 flex-shrink-0" />
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <span v-if="selectedEvent.start.dateTime">
            {{ formatDate(selectedEvent.start.dateTime) }}
            {{ formatTime(selectedEvent.start.dateTime) }} -
            {{ formatTime(selectedEvent.end.dateTime) }}
          </span>
          <span v-else>
            {{ t('CALENDAR.ALL_DAY') }}
          </span>
        </div>
        <div
          v-if="selectedEvent.location"
          class="flex items-center gap-1.5 text-xs text-n-slate-10"
        >
          <span class="i-lucide-map-pin size-3 flex-shrink-0" />
          <span class="truncate">{{ selectedEvent.location }}</span>
        </div>
        <div
          v-if="selectedEvent.attendees?.length"
          class="flex flex-col gap-0.5 mt-1"
        >
          <span class="text-xs text-n-slate-9">
            {{ t('CALENDAR.ATTENDEES') }}
          </span>
          <div
            v-for="a in selectedEvent.attendees"
            :key="a.email"
            class="flex items-center gap-1.5 text-xs text-n-slate-11"
          >
            <span
              class="size-1.5 rounded-full flex-shrink-0"
              :class="{
                'bg-green-500': a.responseStatus === 'accepted',
                'bg-yellow-500': a.responseStatus === 'tentative',
                'bg-n-slate-7': a.responseStatus === 'needsAction',
                'bg-red-500': a.responseStatus === 'declined',
              }"
            />
            <span class="truncate">{{ a.displayName || a.email }}</span>
          </div>
        </div>
      </div>

      <!-- Events list -->
      <template v-else>
        <!-- Contact-related events -->
        <div v-if="contactEvents.length" class="flex flex-col gap-1">
          <span class="text-xs font-medium text-n-slate-9 mb-0.5">
            {{ t('CALENDAR.SIDEBAR.CONTACT_EVENTS') }}
          </span>
          <button
            v-for="event in contactEvents"
            :key="event.id"
            class="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-n-alpha-2 transition-colors text-left"
            @click="selectedEvent = event"
          >
            <div
              class="size-1.5 rounded-full flex-shrink-0"
              :class="isUpcoming(event) ? 'bg-green-500' : 'bg-n-slate-7'"
            />
            <div class="flex-1 min-w-0">
              <div class="text-xs text-n-slate-12 truncate">
                {{ event.summary }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ formatDate(event.start.dateTime || event.start.date) }}
                <template v-if="event.start.dateTime">
                  {{ formatTime(event.start.dateTime) }}
                </template>
              </div>
            </div>
          </button>
        </div>

        <!-- Other upcoming events (collapsed) -->
        <details v-if="allEvents.length" class="mt-1">
          <summary
            class="text-xs text-n-slate-9 cursor-pointer hover:text-n-slate-11 select-none"
          >
            {{
              t('CALENDAR.SIDEBAR.OTHER_EVENTS', { count: allEvents.length })
            }}
          </summary>
          <div class="flex flex-col gap-1 mt-1">
            <button
              v-for="event in allEvents.slice(0, 5)"
              :key="event.id"
              class="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-n-alpha-2 transition-colors text-left"
              @click="selectedEvent = event"
            >
              <div class="text-xs text-n-slate-11 truncate flex-1">
                {{ event.summary }}
              </div>
              <div class="text-xs text-n-slate-10 flex-shrink-0">
                {{ formatDate(event.start.dateTime || event.start.date) }}
              </div>
            </button>
          </div>
        </details>

        <!-- No events -->
        <div
          v-if="!contactEvents.length && !allEvents.length"
          class="py-2 text-xs text-n-slate-10"
        >
          {{ t('CALENDAR.SIDEBAR.NO_EVENTS') }}
        </div>
      </template>
    </template>
  </div>
</template>
